import { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Label,
  TextInput,
  Select,
  ToggleSwitch,
} from "flowbite-react";

const METRIC_TYPES = [
  { value: "count", label: "Count" },
  { value: "sum", label: "Sum" },
  { value: "unique_count", label: "Unique Count" },
  { value: "average", label: "Average" },
];

const AGGREGATION_PERIODS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

/**
 * Modal for creating or editing a metric definition.
 * Available events dropdown populated from the connected provider via Nango.
 */
export const MetricModal = ({
  show,
  onClose,
  editingMetric,
  availableEvents,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    source_event_names: [],
    metric_type: "count",
    aggregation_period: "daily",
    unit: "",
    is_higher_better: true,
    is_feature_metric: true, // New custom metrics default to feature usage
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingMetric) {
      setFormData({
        name: editingMetric.name,
        display_name: editingMetric.display_name,
        source_event_names: editingMetric.source_event_names || [],
        metric_type: editingMetric.metric_type,
        aggregation_period: editingMetric.aggregation_period,
        unit: editingMetric.unit || "",
        is_higher_better: editingMetric.is_higher_better ?? true,
        is_feature_metric: editingMetric.is_feature_metric ?? false,
      });
    } else {
      setFormData({
        name: "",
        display_name: "",
        source_event_names: [],
        metric_type: "count",
        aggregation_period: "daily",
        unit: "",
        is_higher_better: true,
        is_feature_metric: true, // New custom metrics default to feature
      });
    }
  }, [editingMetric, show]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(formData);
    } catch {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onClose={onClose}>
      <Modal.Header>
        {editingMetric ? "Edit Metric" : "Add Custom Metric"}
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Internal Name</Label>
            <TextInput
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="e.g., weekly_active_users"
              disabled={!!editingMetric}
            />
          </div>
          <div>
            <Label htmlFor="display_name">Display Name</Label>
            <TextInput
              id="display_name"
              value={formData.display_name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  display_name: e.target.value,
                }))
              }
              placeholder="e.g., Weekly Active Users"
            />
          </div>
          <div>
            <Label htmlFor="event">Source Event</Label>
            {availableEvents?.length > 0 ? (
              <Select
                id="event"
                value={formData.source_event_names[0] || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    source_event_names: e.target.value ? [e.target.value] : [],
                  }))
                }
              >
                <option value="">Select event</option>
                {availableEvents.map((event) => (
                  <option key={event.event_type} value={event.event_type}>
                    {event.event_type}
                    {event.description ? ` — ${event.description}` : ""}
                  </option>
                ))}
              </Select>
            ) : (
              <TextInput
                id="event"
                value={formData.source_event_names[0] || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    source_event_names: e.target.value ? [e.target.value] : [],
                  }))
                }
                placeholder="e.g., session_start"
              />
            )}
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="metric_type">Metric Type</Label>
              <Select
                id="metric_type"
                value={formData.metric_type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    metric_type: e.target.value,
                  }))
                }
              >
                {METRIC_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <Label htmlFor="period">Period</Label>
              <Select
                id="period"
                value={formData.aggregation_period}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    aggregation_period: e.target.value,
                  }))
                }
              >
                {AGGREGATION_PERIODS.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="unit">Unit</Label>
              <TextInput
                id="unit"
                value={formData.unit}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, unit: e.target.value }))
                }
                placeholder="e.g., users, events, %"
              />
            </div>
            <div className="flex items-end pb-1">
              <ToggleSwitch
                checked={formData.is_higher_better}
                label="Higher is better"
                onChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    is_higher_better: checked,
                  }))
                }
              />
            </div>
          </div>
          <div>
            <ToggleSwitch
              checked={formData.is_feature_metric}
              label="Feature usage metric"
              onChange={(checked) =>
                setFormData((prev) => ({
                  ...prev,
                  is_feature_metric: checked,
                }))
              }
            />
            <p className="mt-1 text-xs text-gray-500">
              Feature metrics appear in the Feature Adoption section of customer
              pages.
            </p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          color="blue"
          onClick={handleSubmit}
          disabled={saving || !formData.name || !formData.display_name}
          isProcessing={saving}
        >
          {editingMetric ? "Save Changes" : "Create Metric"}
        </Button>
        <Button color="gray" onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
